/**
 * VoiceMode — Real-Time Voice Conversation via OpenAI Realtime API
 *
 * Uses WebRTC for direct browser ↔ OpenAI audio streaming.
 * The backend provides an ephemeral session token and loads the
 * replica's full context (personality, name, expertise, etc.)
 * as the system prompt for the voice session.
 *
 * Key fixes applied:
 *  - React Strict Mode guard with proper AbortController lifecycle
 *  - Audio element appended to DOM for autoplay policy compliance
 *  - getUserMedia error surfaced to user
 *  - ICE connection state monitoring for real connection status
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Mic, Volume2, PhoneOff } from 'lucide-react';
import { getApiUrl } from '../lib/env';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = getApiUrl();

type VoiceState = 'connecting' | 'listening' | 'speaking' | 'thinking' | 'error';

interface VoiceModeProps {
    replicaId: string;
    replicaName: string;
    onClose: () => void;
}

export default function VoiceMode({ replicaId, replicaName, onClose }: VoiceModeProps) {
    const { user } = useAuth();
    const [state, setState] = useState<VoiceState>('connecting');
    const [transcript, setTranscript] = useState('');
    const [aiTranscript, setAiTranscript] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isConnected, setIsConnected] = useState(false);

    // Shared refs
    const aiTranscriptRef = useRef('');
    const streamRef = useRef<MediaStream | null>(null);
    const ephemeralKeyRef = useRef<string | null>(null);
    const replicaIdRef = useRef<string>(replicaId);

    // AbortController ref — kills in-flight fetches + signals stale connections
    const abortRef = useRef<AbortController | null>(null);
    // Guard against double-mount in React Strict Mode
    const mountedRef = useRef(false);

    // OpenAI Realtime refs
    const pcRef = useRef<RTCPeerConnection | null>(null);
    const dcRef = useRef<RTCDataChannel | null>(null);
    const audioElRef = useRef<HTMLAudioElement | null>(null);

    // ═══════════════════════════════════════════════════════════
    // DISCONNECT — Clean up everything
    // ═══════════════════════════════════════════════════════════
    const disconnect = useCallback(() => {
        console.log('[VoiceMode] Disconnecting — cleaning up all resources');

        // 1. Abort any in-flight fetch requests
        abortRef.current?.abort();
        abortRef.current = null;

        // 2. OpenAI Realtime cleanup
        if (dcRef.current) {
            dcRef.current.onopen = null;
            dcRef.current.onmessage = null;
            dcRef.current.onclose = null;
            dcRef.current.close();
            dcRef.current = null;
        }
        if (pcRef.current) {
            pcRef.current.ontrack = null;
            pcRef.current.oniceconnectionstatechange = null;
            pcRef.current.close();
            pcRef.current = null;
        }
        if (audioElRef.current) {
            audioElRef.current.pause();
            audioElRef.current.srcObject = null;
            audioElRef.current.load(); // force resource release
            // Remove from DOM if appended
            audioElRef.current.remove();
            audioElRef.current = null;
        }

        // 3. Stop microphone tracks
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }

        setIsConnected(false);
    }, []);

    // ═══════════════════════════════════════════════════════════
    // CONNECT TO OPENAI REALTIME API (WebRTC)
    // ═══════════════════════════════════════════════════════════
    const connect = useCallback(async () => {
        // Clean up any previous connection first
        disconnect();

        const ac = new AbortController();
        abortRef.current = ac;
        const signal = ac.signal;

        setState('connecting');
        setError(null);

        try {
            const token = (await user?.getIdToken?.()) || null;
            if (signal.aborted) return;

            console.log('[VoiceMode] Getting ephemeral token for OpenAI Realtime...');

            // Step 1: Get ephemeral session token — sends replicaId so
            // the backend loads the full replica context as instructions
            const sessionRes = await fetch(`${API_BASE}/chat/realtime-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ replicaId, voice: 'shimmer' }),
                signal,
            });

            if (!sessionRes.ok) {
                const errData = await sessionRes.json().catch(() => ({}));
                throw new Error(errData.error || `Session creation failed (${sessionRes.status})`);
            }

            const sessionData = await sessionRes.json();
            const ephemeralKey = sessionData.client_secret?.value;
            const replicaInstructions = sessionData._replicaInstructions || null;
            const replicaVoice = sessionData._replicaVoice || 'shimmer';
            ephemeralKeyRef.current = ephemeralKey;

            if (!ephemeralKey) throw new Error('No ephemeral key received');
            if (signal.aborted) return;

            console.log('[VoiceMode] Session created for replica:', replicaId,
                '— instructions:', replicaInstructions ? `${replicaInstructions.length} chars` : 'NONE',
                '— connecting via WebRTC...');

            // Step 2: Capture microphone FIRST (needs user gesture context)
            let ms: MediaStream;
            try {
                ms = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true,
                    },
                });
            } catch (micErr: any) {
                console.error('[VoiceMode] Microphone access failed:', micErr);
                if (micErr.name === 'NotAllowedError' || micErr.name === 'PermissionDeniedError') {
                    throw new Error('Microphone access denied. Please allow microphone access in your browser settings and try again.');
                } else if (micErr.name === 'NotFoundError') {
                    throw new Error('No microphone found. Please connect a microphone and try again.');
                } else {
                    throw new Error(`Microphone error: ${micErr.message}`);
                }
            }
            if (signal.aborted) { ms.getTracks().forEach(t => t.stop()); return; }
            streamRef.current = ms;
            console.log('[VoiceMode] Microphone captured:', ms.getTracks().length, 'tracks');

            // Step 3: Create RTCPeerConnection
            const pc = new RTCPeerConnection({
                iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
            });
            pcRef.current = pc;

            // Abort handler: close PC if signal fires
            signal.addEventListener('abort', () => pc.close(), { once: true });

            // Monitor ICE connection state for real status
            pc.oniceconnectionstatechange = () => {
                const iceState = pc.iceConnectionState;
                console.log('[VoiceMode] ICE connection state:', iceState);
                if (signal.aborted) return;

                if (iceState === 'disconnected' || iceState === 'failed') {
                    setError('Voice connection lost. Try again.');
                    setState('error');
                    setIsConnected(false);
                } else if (iceState === 'closed') {
                    setIsConnected(false);
                }
            };

            // Step 4: Audio playback — create element AND append to DOM
            const audioEl = document.createElement('audio');
            audioEl.autoplay = true;
            audioEl.style.display = 'none';
            document.body.appendChild(audioEl); // Required for autoplay in some browsers
            audioElRef.current = audioEl;

            pc.ontrack = (e) => {
                if (signal.aborted) return;
                console.log('[VoiceMode] Received remote audio track');
                audioEl.srcObject = e.streams[0];
                // Force play in case autoplay was blocked
                audioEl.play().catch(err => {
                    console.warn('[VoiceMode] Autoplay blocked:', err.message);
                });
            };

            // Step 5: Add microphone track to peer connection
            const audioTrack = ms.getAudioTracks()[0];
            if (!audioTrack) throw new Error('No audio track available from microphone');
            pc.addTrack(audioTrack, ms);
            console.log('[VoiceMode] Added audio track to peer connection:', audioTrack.label);

            // Step 6: Data channel for events
            const dc = pc.createDataChannel('oai-events');
            dcRef.current = dc;

            dc.onopen = () => {
                if (signal.aborted) return;
                console.log('[VoiceMode] Data channel open — session is live!');
                setIsConnected(true);
                setState('listening');

                // Send session.update to apply the replica's personality/instructions
                if (replicaInstructions) {
                    const voiceTools = [
                        {
                            type: 'function',
                            name: 'generate_content',
                            description: 'ONLY call this when the user explicitly asks to write, draft, or generate content: a meme, joke, email, poem, social post, or any creative text. Do NOT call for general conversation.',
                            parameters: { type: 'object', properties: { request: { type: 'string', description: 'Exactly what to write.' } }, required: ['request'] },
                        },
                        {
                            type: 'function',
                            name: 'write_code',
                            description: 'ONLY call this when the user explicitly asks to write code, build a script, or develop a feature. Examples: "write me a C# script", "build a Python function". Do NOT call for questions about code.',
                            parameters: { type: 'object', properties: { request: { type: 'string', description: 'What to code.' }, language: { type: 'string', description: 'Language or framework if specified.' } }, required: ['request'] },
                        },
                        {
                            type: 'function',
                            name: 'create_task',
                            description: 'ONLY call when user explicitly says "add a task", "create a to-do", or "remind me to". Do NOT call for any other reason.',
                            parameters: { type: 'object', properties: { title: { type: 'string', description: 'Task title.' }, priority: { type: 'string', enum: ['LOW', 'MEDIUM', 'HIGH'] } }, required: ['title'] },
                        },
                        {
                            type: 'function',
                            name: 'search_knowledge',
                            description: 'ONLY call when user asks "what do you know about X" or "search your training/memory for Y". Do NOT call for general questions.',
                            parameters: { type: 'object', properties: { query: { type: 'string', description: 'What to search for.' } }, required: ['query'] },
                        },
                    ];
                    const sessionUpdate = {
                        type: 'session.update',
                        session: {
                            instructions: replicaInstructions,
                            voice: replicaVoice,
                            input_audio_transcription: { model: 'gpt-4o-transcribe' },
                            turn_detection: {
                                type: 'server_vad',
                                threshold: 0.5,
                                silence_duration_ms: 800,
                                prefix_padding_ms: 300,
                            },
                            tools: voiceTools,
                            tool_choice: 'auto',
                        },
                    };
                    dc.send(JSON.stringify(sessionUpdate));
                    console.log('[VoiceMode] Sent session.update with instructions + tools (' + replicaInstructions.length + ' chars)');
                } else {
                    console.warn('[VoiceMode] No replica instructions — using generic persona');
                }

                // Trigger a greeting so the user knows audio is working
                dc.send(JSON.stringify({
                    type: 'response.create',
                    response: {
                        modalities: ['audio', 'text'],
                        instructions: 'Greet the user briefly. Say something like "Hey, I\'m here. What\'s up?" — keep it short and natural, 1-2 sentences max.',
                    },
                }));
                console.log('[VoiceMode] Triggered greeting response');
            };

            dc.onmessage = (e) => {
                if (signal.aborted) return;
                try {
                    handleRealtimeEvent(JSON.parse(e.data));
                } catch { /* ignore parse errors */ }
            };

            dc.onclose = () => {
                if (!signal.aborted) { setError('Connection lost'); setState('error'); }
            };

            // Step 7: SDP negotiation
            const offer = await pc.createOffer();
            if (signal.aborted) return;
            await pc.setLocalDescription(offer);
            if (signal.aborted) return;

            const sdpRes = await fetch(
                'https://api.openai.com/v1/realtime?model=gpt-realtime-1.5',
                {
                    method: 'POST',
                    body: offer.sdp,
                    headers: { Authorization: `Bearer ${ephemeralKey}`, 'Content-Type': 'application/sdp' },
                    signal,
                },
            );

            if (!sdpRes.ok) {
                const errText = await sdpRes.text().catch(() => '');
                console.error('[VoiceMode] WebRTC negotiation failed:', sdpRes.status, errText);
                throw new Error(`Voice connection failed (${sdpRes.status}). ${errText ? 'Details: ' + errText.slice(0, 100) : 'Please try again.'}`);
            }
            if (signal.aborted) return;

            const sdpAnswer = await sdpRes.text();
            await pc.setRemoteDescription({ type: 'answer', sdp: sdpAnswer });
            console.log('[VoiceMode] WebRTC connected! ICE state:', pc.iceConnectionState);

        } catch (err: any) {
            if (signal.aborted || err.name === 'AbortError') {
                console.log('[VoiceMode] Connection aborted (cleanup)');
                return;
            }
            console.error('[VoiceMode] Connection error:', err);
            setError(err.message || 'Failed to connect');
            setState('error');
        }
    }, [user, replicaId, disconnect]);

    // ═══════════════════════════════════════════════════════════
    // OPENAI REALTIME EVENT HANDLER
    // ═══════════════════════════════════════════════════════════
    function handleRealtimeEvent(event: any) {
        switch (event.type) {
            case 'session.created':
                console.log('[VoiceMode] Session created by OpenAI');
                break;
            case 'session.updated':
                console.log('[VoiceMode] ✅ Session updated — instructions + tools applied');
                break;
            case 'input_audio_buffer.speech_started':
                setState('listening');
                setTranscript('');
                break;
            case 'conversation.item.input_audio_transcription.completed':
                if (event.transcript) setTranscript(event.transcript);
                break;
            case 'response.created':
                setState('speaking');
                aiTranscriptRef.current = '';
                setAiTranscript('');
                break;
            case 'response.audio_transcript.delta':
                if (event.delta) {
                    aiTranscriptRef.current += event.delta;
                    setAiTranscript(aiTranscriptRef.current);
                }
                break;
            case 'response.audio_transcript.done':
            case 'response.done':
                setState('listening');
                break;
            case 'response.output_item.done': {
                // Function call completed — execute the tool and feed result back
                const item = event.item;
                if (item?.type === 'function_call' && item?.name && item?.arguments) {
                    setState('thinking');
                    setAiTranscript(`\u{1F9E0} Executing: ${item.name}...`);
                    const callId = item.call_id;
                    let parsedArgs: any = {};
                    try { parsedArgs = JSON.parse(item.arguments); } catch { parsedArgs = { request: item.arguments }; }

                    // Call our backend to execute the tool
                    const token = ephemeralKeyRef.current || '';
                    fetch(`${API_BASE}/chat/voice-action`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({ tool: item.name, args: parsedArgs, replicaId: replicaIdRef.current }),
                        credentials: 'include',
                    }).then(r => r.json()).then((data: any) => {
                        const result = data.result || 'Done.';
                        const dc = dcRef.current;
                        if (!dc || dc.readyState !== 'open') return;

                        // Feed result back to voice model as a function call output
                        dc.send(JSON.stringify({
                            type: 'conversation.item.create',
                            item: {
                                type: 'function_call_output',
                                call_id: callId,
                                output: result,
                            },
                        }));
                        // Ask the model to respond based on the result
                        dc.send(JSON.stringify({ type: 'response.create' }));
                        setAiTranscript('');
                    }).catch(err => {
                        console.error('[VoiceMode] Tool call failed:', err);
                        setState('listening');
                    });
                }
                break;
            }
            case 'error':
                console.error('[VoiceMode] Realtime error:', event.error);
                if (event.error?.message) setError(event.error.message);
                break;
        }
    }

    // ═══════════════════════════════════════════════════════════
    // LIFECYCLE — Guard against React Strict Mode double-mount
    // ═══════════════════════════════════════════════════════════
    useEffect(() => {
        // In Strict Mode dev, first mount+unmount is a probe.
        // Only connect on the real mount.
        if (mountedRef.current) {
            // This is the re-mount after strict mode cleanup
            connect();
        } else {
            mountedRef.current = true;
            connect();
        }
        return () => {
            disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleClose() {
        disconnect();
        onClose();
    }

    // ═══════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════
    const stateLabels: Record<VoiceState, string> = {
        connecting: 'Connecting...',
        listening: 'Listening...',
        speaking: `${replicaName} is speaking...`,
        thinking: 'Working on it...',
        error: 'Connection error',
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-xl"
            style={{ animation: 'voiceFadeIn 0.3s ease-out' }}>

            {/* Close */}
            <button onClick={handleClose}
                className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10">
                <X size={20} />
            </button>

            {/* Header */}
            <div className="absolute top-8 left-1/2 -translate-x-1/2 text-center">
                <p className="text-white/90 text-lg font-medium">{replicaName}</p>
                <p className="text-white/40 text-sm mt-1">
                    {isConnected ? '● Live Voice' : 'Voice Mode'}
                </p>
            </div>

            {/* Main */}
            <div className="flex flex-col items-center gap-8">
                {/* Animated orb */}
                <div className="relative w-48 h-48 flex items-center justify-center">
                    {state === 'connecting' && (
                        <div className="absolute inset-4 rounded-full border-2 border-dashed border-amber-400/40 animate-spin" style={{ animationDuration: '3s' }} />
                    )}
                    {state === 'listening' && (
                        <>
                            <div className="absolute inset-0 rounded-full border-2 border-hig-accent/20 animate-pulse" style={{ animationDuration: '3s' }} />
                            <div className="absolute inset-4 rounded-full border-2 border-hig-accent/15 animate-pulse" style={{ animationDuration: '2.5s' }} />
                            <div className="absolute inset-8 rounded-full border-2 border-hig-accent/10 animate-pulse" style={{ animationDuration: '2s' }} />
                        </>
                    )}
                    {state === 'speaking' && (
                        <>
                            <div className="absolute inset-0 rounded-full border-2 border-purple-400/30 animate-ping" style={{ animationDuration: '1.5s' }} />
                            <div className="absolute inset-4 rounded-full border-2 border-purple-400/25 animate-ping" style={{ animationDuration: '1.2s' }} />
                            <div className="absolute inset-8 rounded-full border-2 border-purple-400/20 animate-ping" style={{ animationDuration: '0.9s' }} />
                        </>
                    )}

                    <div className={`relative w-28 h-28 rounded-full flex items-center justify-center transition-all duration-500 ${state === 'connecting'
                        ? 'bg-gradient-to-br from-amber-500/80 to-orange-600/80'
                        : state === 'listening'
                            ? 'bg-gradient-to-br from-blue-500 to-cyan-500 shadow-[0_0_80px_rgba(59,130,246,0.4)]'
                            : state === 'speaking'
                                ? 'bg-gradient-to-br from-purple-500 to-pink-500 shadow-[0_0_80px_rgba(168,85,247,0.4)]'
                                : 'bg-gradient-to-br from-red-500/80 to-red-700/80'
                        }`}>
                        {state === 'connecting' ? (
                            <div className="w-8 h-8 border-[3px] border-white/30 border-t-white rounded-full animate-spin" />
                        ) : state === 'listening' ? (
                            <Mic size={36} className="text-white drop-shadow-lg" />
                        ) : state === 'speaking' ? (
                            <Volume2 size={36} className="text-white drop-shadow-lg animate-pulse" />
                        ) : (
                            <PhoneOff size={36} className="text-white drop-shadow-lg" />
                        )}
                    </div>
                </div>

                {/* State label */}
                <p className={`text-lg font-medium transition-colors ${state === 'connecting' ? 'text-amber-400'
                    : state === 'listening' ? 'text-hig-accent'
                        : state === 'speaking' ? 'text-purple-400'
                            : 'text-red-400'
                    }`}>
                    {stateLabels[state]}
                </p>

                {/* Live transcripts */}
                <div className="flex flex-col items-center gap-3 min-h-[100px]">
                    {transcript && (
                        <div className="max-w-md px-6 py-3 bg-white/5 rounded-2xl border border-white/10">
                            <p className="text-sm text-white/40 mb-1">You:</p>
                            <p className="text-white/90 text-sm">{transcript}</p>
                        </div>
                    )}
                    {aiTranscript && (
                        <div className="max-w-md px-6 py-3 bg-purple-500/10 rounded-2xl border border-purple-500/20">
                            <p className="text-sm text-purple-300/60 mb-1">{replicaName}:</p>
                            <p className="text-white/90 text-sm leading-relaxed">
                                {aiTranscript.length > 200 ? aiTranscript.slice(0, 200) + '...' : aiTranscript}
                            </p>
                        </div>
                    )}
                </div>

                {/* Error + retry */}
                {error && (
                    <div className="flex flex-col items-center gap-3">
                        <div className="max-w-md px-4 py-2 bg-red-500/10 rounded-xl border border-red-500/20">
                            <p className="text-red-400 text-sm">{error}</p>
                        </div>
                        <button onClick={() => connect()}
                            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm transition-colors">
                            Retry Connection
                        </button>
                    </div>
                )}
            </div>

            {/* End call */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
                <button onClick={handleClose}
                    className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors shadow-lg shadow-red-500/30">
                    <PhoneOff size={24} />
                </button>
                <p className="text-white/30 text-xs">End call</p>
            </div>

            <style>{`
        @keyframes voiceFadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
        </div>
    );
}
